import { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelection } from '../../context/SelectionContext';
import { useCourses } from '../../hooks/useCourses';
import { useChildren } from '../../hooks/useChildren';
import { useDragScroll } from '../../hooks/useDragScroll';

const FADE_DURATION = 150;

// Check if course is a report-only course (e.g., "2025 Fall Midterm")
const isReportCourse = (course) => {
  const pattern = /^\d{4}\s+(Spring|Summer|Fall|Winter)\s+(Midterm|Final)$/i;
  return pattern.test(course.course_title?.trim() || '');
};

// Format semester label for display (e.g., "Spring 2026" or "2026 봄")
const formatSemesterLabel = (semester, t) => {
  const season = t(`seasons.${semester.season}`);
  return `${season} ${semester.year}`;
};

export default function CourseSelector() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedChild, selectedCourse, selectCourse, getSavedCourseId } = useSelection();
  const { children, loading: childrenLoading } = useChildren();
  const { ref: semesterScrollRef } = useDragScroll();
  const { ref: courseScrollRef } = useDragScroll();

  // Only fetch courses if selected child is valid (belongs to this parent)
  const isValidChild = selectedChild && children.some(c => c.id === selectedChild.id);
  const { courses, semesters, loading: coursesLoading, error } = useCourses(isValidChild ? selectedChild?.id : null);

  // Consider loading if either children or courses are loading
  const loading = childrenLoading || coursesLoading;

  // Local state for selected semester
  const [selectedSemester, setSelectedSemester] = useState(null);

  // Filter out report-only courses
  const filteredCourses = useMemo(() => {
    return courses.filter(course => !isReportCourse(course));
  }, [courses]);

  // Get courses for selected semester, sorted by start_date descending
  const coursesForSemester = useMemo(() => {
    if (!selectedSemester) return [];

    return filteredCourses
      .filter(course => course.semesters?.some(sem => sem.id === selectedSemester.id))
      .sort((a, b) => {
        const dateA = new Date(a.start_date || 0);
        const dateB = new Date(b.start_date || 0);
        return dateB - dateA; // Latest first
      });
  }, [filteredCourses, selectedSemester]);

  // Auto-select first semester when semesters load
  useEffect(() => {
    if (semesters.length > 0 && !selectedSemester) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing semester selection on data load
      setSelectedSemester(semesters[0]);
    } else if (semesters.length === 0 && selectedSemester) {
      setSelectedSemester(null);
    }
  }, [semesters, selectedSemester]);

  // Reset semester when child changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting semester when child changes
    setSelectedSemester(null);
  }, [selectedChild?.id]);

  // Auto-select course when semester changes or courses load
  useEffect(() => {
    if (coursesForSemester.length > 0) {
      // Check for course ID in URL query param (from auto-login redirect)
      const courseIdParam = searchParams.get('course');
      if (courseIdParam) {
        const targetCourse = coursesForSemester.find(c => c.id === parseInt(courseIdParam, 10));
        if (targetCourse) {
          selectCourse(targetCourse);
          // Clean up URL by removing the course query param
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('course');
          const newSearch = newParams.toString();
          navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
          return;
        }
      }

      if (!selectedCourse || !coursesForSemester.find(c => c.id === selectedCourse.id)) {
        // Try to restore saved course for this child
        const savedCourseId = selectedChild?.id ? getSavedCourseId(selectedChild.id) : null;
        if (savedCourseId) {
          const savedCourse = coursesForSemester.find(c => c.id === savedCourseId);
          if (savedCourse) {
            selectCourse(savedCourse);
            return;
          }
        }
        // Fallback to first course in this semester
        selectCourse(coursesForSemester[0]);
      }
    } else if (coursesForSemester.length === 0 && selectedCourse && !loading) {
      selectCourse(null);
    }
  }, [coursesForSemester, selectedCourse, selectCourse, loading, selectedChild?.id, getSavedCourseId, searchParams, navigate, location.pathname]);

  // Reset selected course when courses for semester change and current selection no longer exists
  useEffect(() => {
    if (coursesForSemester.length > 0 && selectedCourse) {
      const courseStillExists = coursesForSemester.find(c => c.id === selectedCourse.id);
      if (!courseStillExists) {
        selectCourse(coursesForSemester[0]);
      }
    }
  }, [coursesForSemester, selectedCourse, selectCourse]);

  // Fade transition state
  const [opacity, setOpacity] = useState(1);
  const [displayLoading, setDisplayLoading] = useState(loading);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip transition on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing display loading on first render
      setDisplayLoading(loading);
      return;
    }

    // Fade out, swap content, fade in
    setOpacity(0);
    const timer = setTimeout(() => {
      setDisplayLoading(loading);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setOpacity(1);
        });
      });
    }, FADE_DURATION);

    return () => clearTimeout(timer);
  }, [loading]);

  if (!selectedChild) {
    return null;
  }

  const getButtonClasses = (isSelected) => {
    if (isSelected) {
      return 'bg-[#589DD5] text-white shadow-sm';
    }
    return 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:border-[#589DD5] hover:text-[#589DD5]';
  };

  const renderSemesterButton = (semester) => {
    const isSelected = selectedSemester?.id === semester.id;

    return (
      <button
        key={semester.id}
        onClick={() => setSelectedSemester(semester)}
        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${getButtonClasses(isSelected)}`}
      >
        {formatSemesterLabel(semester, t)}
      </button>
    );
  };

  const renderCourseButton = (course) => {
    const isSelected = selectedCourse?.id === course.id;

    return (
      <button
        key={course.id}
        onClick={() => selectCourse(course)}
        className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${getButtonClasses(isSelected)}`}
      >
        {course.course_title}
      </button>
    );
  };

  const renderSkeletonRow = (label) => (
    <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
      <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{label}</span>
      <div className="flex gap-1.5 sm:gap-2">
        <div className="h-[28px] sm:h-[36px] w-20 sm:w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        <div className="h-[28px] sm:h-[36px] w-24 sm:w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    </div>
  );

  const renderSemesterRow = () => {
    if (displayLoading) {
      return (
        <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
          {renderSkeletonRow(t('selector.semester'))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
          <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
            <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{t('selector.semester')}</span>
            <span className="text-red-600 dark:text-red-400 text-xs sm:text-sm">{error}</span>
          </div>
        </div>
      );
    }

    if (semesters.length === 0) {
      return (
        <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700">
          <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
            <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{t('selector.semester')}</span>
            <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">{t('selector.noSemesters')}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={semesterScrollRef}
        className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 border-b border-[#E5E5E5] dark:border-gray-700 overflow-x-auto scrollbar-hide"
      >
        <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
          <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{t('selector.semester')}</span>
          <div className="flex gap-1.5 sm:gap-2 pr-4">
            {semesters.map(semester => renderSemesterButton(semester))}
          </div>
        </div>
      </div>
    );
  };

  const renderCourseRow = () => {
    if (displayLoading) {
      return (
        <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20">
          {renderSkeletonRow(t('selector.course'))}
        </div>
      );
    }

    if (!selectedSemester || semesters.length === 0) {
      return null;
    }

    if (coursesForSemester.length === 0) {
      return (
        <div className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20">
          <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
            <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{t('selector.course')}</span>
            <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">{t('selector.noCourses')}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={courseScrollRef}
        className="px-4 py-2.5 sm:px-6 sm:py-3 md:px-12 lg:px-20 overflow-x-auto scrollbar-hide"
      >
        <div className="flex items-center gap-2 sm:gap-3 h-[28px] sm:h-[36px]">
          <span className="text-[10px] sm:text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider shrink-0 w-12 sm:w-16 text-center">{t('selector.course')}</span>
          <div className="flex gap-1.5 sm:gap-2 pr-4">
            {coursesForSemester.map(course => renderCourseButton(course))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="transition-opacity ease-in-out"
      style={{ opacity, transitionDuration: `${FADE_DURATION}ms` }}
    >
      {renderSemesterRow()}
      {renderCourseRow()}
    </div>
  );
}
